import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AlertDialog } from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/text-area'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useUserContactMutation } from '@/store/api/Users/Users.query'
import { BackgroundCheckContactUsSchema } from '@/utils/schemas'
import type { BackgroundCheckContactUsType } from '@/utils/schemas'

import { ContactUsAlertDialog } from '../Home/components/ContactUsAlertDialog/ContactUsAlertDialog'

export const ContactUs = () => {
  const { t } = useTranslation()

  const [submitContact, { isLoading }] = useUserContactMutation()
  const [isOpen, setIsOpen] = useState(false)

  const form = useForm<BackgroundCheckContactUsType>({
    resolver: zodResolver(BackgroundCheckContactUsSchema),
    defaultValues: { comment: '' },
  })

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form

  const onSubmit = async (data: BackgroundCheckContactUsType) => {
    try {
      await submitContact({ message: data.comment })
      setIsOpen(true)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.formError'))
    }
  }

  return (
    <WeScreen
      useHeader={false}
      useNextButton={false}
      contentClassName="md:justify-center"
    >
      <AlertDialog open={isOpen}>
        <div className="mx-auto w-full p-4 md:w-3/4 md:p-6">
          <div className="rounded-2xl border border-[#0000001a] bg-baseColorHighlight p-6 dark:border-[#ffffff14] md:p-8">
            <p className="mb-6 text-base font-medium text-neutralColor">
              {t('dashboard.support.titleText')}
            </p>

            <Form {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
              >
                <FormField
                  control={control}
                  name="comment"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <label
                        htmlFor="contact-us-message"
                        className="text-sm font-medium text-neutralColor"
                      >
                        {t('dashboard.support.comment')}
                      </label>
                      <FormControl>
                        <Textarea
                          id="contact-us-message"
                          rows={6}
                          placeholder={t(
                            'dashboard.support.messagePlaceholder',
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-name="comment" />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={!isDirty}
                  >
                    {t('dashboard.support.submit')}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>

        <ContactUsAlertDialog />
      </AlertDialog>
    </WeScreen>
  )
}
